import { getRequest, postRequest } from '../../../../services/httpService';
import { GetIndentListParams, StatusCounts } from '../stockTransfer.helper';
import * as Yup from 'yup';


export interface Column<T> {
  label: string;
  field: keyof T;
}

interface PatientObject {
  id: number;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  medical_record_no?: string;
  gender?: string;
  age?: number;
}

export interface IssueProspectiveListResponse {
  id: number;
  from_clinic: string;
  from_store?: string;
  from_store_id?: string;
  to_clinic: string;
  to_store: string;
  expected_date: string;
  indent_details: IndentDetail[];
  patient: PatientObject | null;
  status: string;
  remark: string | null;
  created_on: string;
  cancel_remark: string | null;
  indent_no: string | null;
  issue_status: string;
  total_amount: string;
  stock_availability: string;
  stock_availability_label?: string;
  from_clinic_label: string;
  from_store_label: string;
  to_clinic_label: string;
  to_store_label: string;
  force_close_remark: string | null;
  rejected_remark: string | null;
  issue_numbers: string[];
  receive_numbers: string[];
  pr_no: string | null;
  po_numbers: string[];
  grn_numbers: string[];
  
}

interface TaxDetail {
  percent: number;
  amount: number;
}
interface Taxes {
  sgst: TaxDetail;
  cgst: TaxDetail;
}
export interface IssuedListResponse {
  id: number;
  clinic: string;
  from_store: string;
  to_store: string;
  expected_date: string;
  issue_item_details: IndentDetail[];
  patient: PatientObject | null;
  remark: string | null;
  created_on: string;
  cancel_remark: string | null;
  issue_no: string;
  indent_no?: string | null;
  total_amount: string;
  clinic_label: string;
  to_clinic_label?: string;
  to_clinic?: string;
  from_store_label: string;
  to_store_label: string;
  issue_type: string;
  issue_type_label: string;
  pr_no?: string | null;
  issue_date?: string | null;
  indent_date?: string | null;
  indent_remark?: string | null;
  Taxes?:Taxes;
  created_by?: string | null;
  approved_by?: string | null;
}

export type ProspectiveRow = {
  id: string | number;
  patient: string;
  indentNo: string;
  indentDate: string | Date;
  fromClinicStore: string;
  toClinicStore: string;
  indentRemark?: string;
  stockAvailability?: string;
  status: React.ReactNode;
  indent_details?: IndentDetail[];
};

export interface IndentBatch {
  id: number;
  batch_number: string;
  expiry_date: string;
  available_stock?: number;
  base_cost?: number;
  base_mrp ?: number; 
  available_uom_name?: string;
  is_hold?: boolean;
}
export interface IssueBatch {
  batch_number: string;
  expiry_date: string;
  cost_price?: string | null;
  mrp ?: string | null; 
  id?: number;
  base_cost_price?: string | null;
  base_mrp ?: string | null;
}
export interface BarcodeInfo {
  batch_id: number;
  batch_code: string;
  item_id: number;
  item_name: string;
  barcode_text: string;
  barcode_image_url: string;
  proxy_image_url: string;
}
export interface IndentDetail {
  id: number;
  item: Item;
  indent_qty: number;
  indent_qty_in_base?: number;
  indent_pending_qty?: number;
  indent_pending_qty_in_base?: number;
  available_qty: number;
  uom: string;
  base_uom_name?: string;
  base_uom_label?: string;
  uom_label: string;
  indent_uom_label?: string;
  batches?: IndentBatch[];
  batch?: IssueBatch;
  base_cost_price?: string | null;
  total_base_cost_price?: string;
  total_mrp_cost_price?: string;
  issue_qty?: number;
  pending_qty?: number;
  available_stock?: number;
  received_qty?: number;
  barcode?: BarcodeInfo;
  to_store_id?: string;
  base_mrp ?: string | null;
  // API response property (nested structure)
  taxes?: Taxes;
  // UI mapped property (same as API)
  Taxes?: Taxes;
}

export interface Item {
  id: number;
  item_code: string;
  cost_price: string;
  is_active: boolean;
  is_perishable: boolean;
  mrp: string;
  name: string;
  brand_name: string;
  is_free: boolean;
  is_stimulation_drug: boolean;
  is_trigger_drug: boolean;
  category: string;
  group: string;
  uom: string;
  item_name?: string;
  code?: string;
  item_group_name ?: string;
  item_category_name ?: string;
  available_qty?: number;
  base_uom_name?: string;

  // Labels
  category_label: string;
  group_label: string;
  uom_label: string;
  name_label: string;
  brand_name_label: string;
  batches?: IndentBatch[];
}

export interface IssueProspectiveListApiResponse {
  objects: IssueProspectiveListResponse[];
  status_counts: StatusCounts;
  meta?: {
    total_count?: number;
    limit?: number;
    offset?: number;
  };
}

export interface IssuedListApiResponse {
  objects: IssuedListResponse[];
  status_counts: StatusCounts;
  meta?: {
    total_count?: number;
    limit?: number;
    offset?: number;
  };
}

export interface IssueDetailPayload {
  item?: number;
  available_stock?: number;
  indent_qty?: number;
  uom?: string | number;
  to_clinic?: number | null;
  to_store?: number | null;
  batch: number | null;
  issue_qty?: number;
  base_cost_price?: number | null;  // Base cost price per unit
  total_base_cost_price?: number | null;
  total_mrp_cost_price?: number | null;
  indent_detail_id?: number;
  base_mrp?: number | null;  // Base MRP (Maximum Retail Price) per unit
}

export interface CreateMultipleIssuePayload {
  clinic: number | null;
  from_store: number | null;
  expected_date: string;
  remark: string | null;
  issue_type?: string;
  status?: string;
  issue_item_details: IssueDetailPayload[];
}

export interface CreateIssuePayload {
 items: IssueDetailPayload[];
}

export interface IssueItemDetail {
  item: number;
  batch: number;
  issue_qty: number;
  indent_qty: number | null;
  received_qty?: number | null;
  available_stock: number;
  base_mrp: string;
  base_cost_price: string;
  total_base_cost_price: string;
  total_mrp_cost_price: string;
  taxes?: Taxes;
}

export interface IssueDataItem {
  clinic: number;
  from_store: number;
  to_store: number;
  expected_date: string;   // ISO date (YYYY-MM-DD)
  issue_item_details: IssueItemDetail[];
  patient: string | null;
  remark: string | null;
}

type CreateIndentApiResponse = {
  message: string;
  count: number;
  data: IssueDataItem[];
};

export type CreateIssueResponse = {
  message: string;
  issue_no: string;
}

export interface StockBatchResponse {
  item: number;
  item_name: string;
  batch: number;
  batch_number: string;
  available_stock: string;   // coming as string from API
  stock_uom: number;
  from_store_id: number;
  to_store_id: number;
  from_clinic_label: string;
  from_store_label: string;
  to_clinic_label: string;
  to_store_label: string;
  category_label: string;
  group_label: string;
  uom_label: string;
  name_label: string;
  base_cost: string;         // string in API
  base_mrp: string;          // string in API
  is_free: boolean;
  item_code?: string;
  expired_date?: string | null;
}

export type StockBatchApiResponse = StockBatchResponse[];



// Yup Validation Schema for Add New Issue
export function createAddIssueValidationSchema() {
  return Yup.object().shape({
    clinic_id: Yup.number().nullable(),
    store_id: Yup.number().nullable(),
    issueDate: Yup.string().required('Issue Date is required'),
    remark: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          available_qty: Yup.number().nullable(),
          requestToClinicId: Yup.number().nullable(),
          requestToStoreId: Yup.number()
            .required('Request To is required')
            .test(
              'not-same-store',
              'Cannot issue to the same clinic and store',
              function (value) {
                // Access the root form values to get the from_store (store_id)
                const fromStoreId = this.from?.[1]?.value?.store_id;
                if (!fromStoreId || !value) return true;
                return value !== fromStoreId;
              }
            ),
          batches: Yup.array()
            .min(1, 'Batch is required')
            .required('Batch is required'),
          qty: Yup.number()
            .typeError('Quantity is required')
            .moreThan(0, 'Quantity should not be zero')
            .required('Quantity is required')
            .test(
              'not-over-available',
              'Issue quantity cannot exceed available stock',
              function (value) {
                const v = typeof value === 'number' ? value : 0;
                const item = this.parent;
                const selectedBatch = Array.isArray(item?.batches) && item.batches.length > 0 ? item.batches[0] : null;
                const maxAvailable = selectedBatch?.available_stock ?? item?.available_qty ?? 0;
                return v <= Number(maxAvailable ?? 0);
              }
            ),
          qtyUnit: Yup.string().required('UOM is required'),
        })
      )
      .min(1, 'Please add at least one item')
      .required('Items are required'),
  });
}


export function getIssueProspectiveList(params: GetIndentListParams = {}): Promise<IssueProspectiveListApiResponse> {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const url = `indents/prospective/?${query.toString()}`;
  return getRequest(url, { responseType: 'json' });
}

export function getIssuedList(params: GetIndentListParams = {}): Promise<IssuedListApiResponse> {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const url = `issue/?${query.toString()}`;
  return getRequest(url, { responseType: 'json' });
}

//create multiple indent API Call
export function createMultipleIssue(data: CreateMultipleIssuePayload): Promise<CreateIndentApiResponse> {
  const url = 'issue/create-multiple/';
  return postRequest(url, data, { responseType: 'json' });
}


export function IndentToCreateIssue(id: number | string, data: CreateIssuePayload): Promise<CreateIssueResponse> {
  const url = `indents/${id}/create-issue/`;
  return postRequest(url, data, { responseType: 'json' });
}

// Parameters for getShowStock API
export interface GetShowStockParams {
  itemIds?: number | number[];
  from_store?: number;
  to_store?: number;
  item_category_id?: string;
  item_group_id?: string;
  search?: string;
}

// Fetches available stock with batch details, optionally filtered by store, category, group, and search
export function getShowStock(params: GetShowStockParams = {}): Promise<StockBatchApiResponse> {
  const query = new URLSearchParams();
  
  // Handle item IDs (special case - can be array)
  if (params.itemIds !== undefined && params.itemIds !== null) {
    const ids = Array.isArray(params.itemIds) ? params.itemIds : [params.itemIds];
    ids.forEach(id => query.append('item_id', String(id)));
  }
  
  // Add other optional parameters using explicit checks to avoid dropping valid values like 0
  if (params.from_store !== undefined && params.from_store !== null) {
    query.append('from_store_id', String(params.from_store));
  }
  if (params.to_store !== undefined && params.to_store !== null) {
    query.append('to_store', String(params.to_store));
  }
  if (params.item_category_id !== undefined && params.item_category_id !== null && params.item_category_id !== '') {
    query.append('item_category_id', params.item_category_id);
  }
  if (params.item_group_id !== undefined && params.item_group_id !== null && params.item_group_id !== '') {
    query.append('item_group_id', params.item_group_id);
  }
  if (params.search !== undefined && params.search !== null && params.search !== '') {
    query.append('search', params.search);
  }
  
  const url = `available-stock-list/available-stock/?${query.toString()}`;
  return getRequest(url, { responseType: 'json' });
}